@if (data(); as f) {
  @let $editing = editing();
  @let $loading = sessionPage.loading();
  @let $dataHasUnits = f.unit_groups.length > 0;
  @let $hasUnits = $editing ? editedUnits().length > 0 : $dataHasUnits;
  @let $hasDuplicateUnits = $editing ? !!duplicateCodeError() : false;

  <ng-template #headerEnd>
    @if (!$editing) {
      <calcite-action
        icon="pencil"
        scale="m"
        text="Edit"
        text-enabled
        (click)="startEditUnits()"
      />
    } @else {
      <calcite-action
        icon="check"
        scale="s"
        text="Save"
        text-enabled
        class="app-success"
        [disabled]="$loading || $hasDuplicateUnits && !$hasUnits"
        (click)="saveUnits()"
      />
      <calcite-action
        icon="x"
        scale="m"
        class="app-danger"
        text="Cancel"
        text-enabled
        [disabled]="$loading || !$dataHasUnits"
        (click)="cancelEditUnits()"
      />
    }
  </ng-template>

  @if (unitsRequired()) {
    <calcite-notice open kind="warning" icon="exclamation-mark-triangle" width="full">
      <div slot="title">Unit data required</div>
      <div slot="message">
        We couldn't automatically determine unit types for this property. Enter them below to continue.
      </div>
    </calcite-notice>
  }

  <div class="facts-grid">
    <calcite-panel heading="Identification" heading-level="3" icon="fingerprint">
      <table class="app-table-calcite app-table-nowrap">
        <tbody>
        <tr>
          <td class="row-label">Address</td>
          <td>{{ sessionPage.session()?.location?.address || '—' }}{{ sessionPage.session()?.location?.city ? ', ' + sessionPage.session()?.location?.city : '' }}</td>
        </tr>
        <tr>
          <td class="row-label">County</td>
          <td>{{ sessionPage.session()?.location?.county ?? '—' }}</td>
        </tr>
        <tr>
          <td class="row-label">APN</td>
          <td>{{ sessionPage.session()?.apn ?? '—' }}</td>
        </tr>
        <tr>
          <td class="row-label">Property Type</td>
          <td>{{ sessionPage.session()?.property_type ?? '—' }}</td>
        </tr>
        <tr>
          <td class="row-label">Effective Year</td>
          <td>{{ f.facts.effective_year ?? '—' }}</td>
        </tr>
        <tr>
          <td class="row-label">Year Built</td>
          <td>{{ f.facts.year_built ?? '—' }}</td>
        </tr>
        <tr>
          <td class="row-label">Rent Control</td>
          <td>
            <span class="app-flex-center rc-status">
              {{ f.rent_control.status | rentControlLabel }}
              @if (f.rent_control.explanation) {
                <calcite-icon id="rent-control-help" icon="information" scale="s"></calcite-icon>
                <calcite-tooltip reference-element="rent-control-help">{{ f.rent_control.explanation }}</calcite-tooltip>
              }
            </span>
          </td>
        </tr>
        </tbody>
      </table>
    </calcite-panel>

    <calcite-panel heading="Building" heading-level="3" icon="urban-model">
      <table class="app-table-calcite app-table-nowrap">
        <tbody>
        <tr>
          <td class="row-label"># of Units</td>
          <td>{{ (f.facts.number_of_units | number) ?? '—' }}</td>
        </tr>
        <tr>
          <td class="row-label">Building Size</td>
          <td>{{ f.facts.building_area_sqft !== null ? ((f.facts.building_area_sqft | number:'1.0-0') + ' sq ft') : '—' }}</td>
        </tr>
        <tr>
          <td class="row-label"># of Buildings</td>
          <td>{{ (f.facts.number_of_buildings | number) ?? '—' }}</td>
        </tr>
        <tr>
          <td class="row-label"># of Floors</td>
          <td>{{ f.facts.number_of_floors ?? '—' }}</td>
        </tr>
        <tr>
          <td class="row-label">Total Bedrooms</td>
          <td>{{ f.facts.total_bedrooms ?? '—' }}</td>
        </tr>
        <tr>
          <td class="row-label">Total Bathrooms</td>
          <td>{{ f.facts.total_bathrooms ?? '—' }}</td>
        </tr>
        </tbody>
      </table>
    </calcite-panel>

    <calcite-panel heading="Units" heading-level="3" icon="floor-plan">
      @if (unitsError()) {
        <div class="app-callout app-error">{{ unitsError() }}</div>
      }
      @if (duplicateCodeError()) {
        <div class="app-callout app-error">{{ duplicateCodeError() }}</div>
      }

      @if (!$editing) {
        @if ($hasUnits) {
          <calcite-table caption="Unit mix" bordered>
            <calcite-table-row slot="table-header">
              <calcite-table-header heading="Type"/>
              <calcite-table-header heading="Bedrooms" alignment="end"/>
              <calcite-table-header heading="Bathrooms" alignment="end"/>
              <calcite-table-header heading="Units" alignment="end"/>
            </calcite-table-row>
            @for (group of f.unit_groups; track group.code) {
              <calcite-table-row>
                <calcite-table-cell>
                  {{ group.label }}
                </calcite-table-cell>
                <calcite-table-cell alignment="end">{{ group.bedrooms | number }}</calcite-table-cell>
                <calcite-table-cell alignment="end">{{ (group.bathrooms | number:'1.0-1') ?? '—' }}</calcite-table-cell>
                <calcite-table-cell alignment="end">{{ group.unit_count | number }}</calcite-table-cell>
              </calcite-table-row>
            }
          </calcite-table>
        } @else {
          <p class="app-muted">No unit mix data available.</p>
        }
      } @else {
        <calcite-table caption="Edit unit mix" bordered>
          <calcite-table-row slot="table-header">
            <calcite-table-header heading="Type"/>
            <calcite-table-header heading="Bedrooms" alignment="end"/>
            <calcite-table-header heading="Bathrooms" alignment="end"/>
            <calcite-table-header heading="Units" alignment="end"/>
            <calcite-table-header heading="" alignment="end"/>
          </calcite-table-row>
          @for (row of editedUnits(); track row.rowId) {
            <calcite-table-row>
              <calcite-table-cell>
                {{ row.bedrooms | unitTypeLabel:row.bathrooms:row.is_manager }}
              </calcite-table-cell>
              <calcite-table-cell alignment="end">
                <calcite-input-number
                  number-button-type="none"
                  [value]="row.bedrooms.toString()"
                  (input)="updateBedrooms(row.rowId, $any($event.target).value)"
                ></calcite-input-number>
              </calcite-table-cell>
              <calcite-table-cell alignment="end">
                <calcite-input-number
                  number-button-type="none"
                  placeholder="—"
                  [value]="row.bathrooms === null ? '' : row.bathrooms.toString()"
                  (input)="updateBathrooms(row.rowId, $any($event.target).value)"
                ></calcite-input-number>
              </calcite-table-cell>
              <calcite-table-cell alignment="end">
                <calcite-input-number
                  number-button-type="none"
                  [value]="row.unit_count.toString()"
                  (input)="updateUnitCount(row.rowId, $any($event.target).value)"
                ></calcite-input-number>
              </calcite-table-cell>
              <calcite-table-cell alignment="end">
                <calcite-button
                  icon-start="trash"
                  appearance="transparent"
                  kind="danger"
                  scale="s"
                  (click)="removeUnitRow(row.rowId)"
                ></calcite-button>
              </calcite-table-cell>
            </calcite-table-row>
          }
        </calcite-table>

        <calcite-action-bar slot="header-actions-end" expanded expand-disabled layout="horizontal">
          <calcite-action scale="s" text="Add" text-enabled icon="cube-plus" (click)="addUnitRow(false)"/>
          <calcite-action
            scale="s"
            text="Add Manager" text-enabled
            icon="user-plus"
            [disabled]="hasManagerRow()"
            (click)="addUnitRow(true)"
          />
        </calcite-action-bar>
      }
    </calcite-panel>

    <calcite-panel heading="Land" heading-level="3" icon="layer-basemap">
      <table class="app-table-calcite app-table-nowrap">
        <tbody>
        <tr>
          <td class="row-label">Land Area</td>
          <td>{{ f.facts.land_area_sqft !== null ? ((f.facts.land_area_sqft | number:'1.0-0') + ' sq ft') : '—' }}</td>
        </tr>
        <tr>
          <td class="row-label">Land-to-Building Ratio</td>
          <td>{{ (f.facts.land_to_building_ratio | number:'1.2-2') ?? '—' }}</td>
        </tr>
        </tbody>
      </table>
    </calcite-panel>

    <calcite-panel heading="Assessed Value" heading-level="3" icon="money">
      <table class="app-table-calcite app-table-nowrap">
        <tbody>
        <tr>
          <td class="row-label">Date of Value</td>
          <td>{{ f.facts.date_of_value ?? '—' }}</td>
        </tr>
        <tr>
          <td class="row-label">Land</td>
          <td>{{ (f.facts.assessed_land | currency:'USD':'symbol':'1.0-0') ?? '—' }}</td>
        </tr>
        <tr>
          <td class="row-label">Improvements</td>
          <td>{{ (f.facts.assessed_improvements | currency:'USD':'symbol':'1.0-0') ?? '—' }}</td>
        </tr>
        <tr>
          <td class="row-label">Other</td>
          <td>{{ (f.facts.assessed_other | currency:'USD':'symbol':'1.0-0') ?? '—' }}</td>
        </tr>
        <tr>
          <td class="row-label">Total</td>
          <td class="total-value">{{ (f.facts.assessed_total | currency:'USD':'symbol':'1.0-0') ?? '—' }}</td>
        </tr>
        </tbody>
      </table>
    </calcite-panel>

  </div>
}
